import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  TextField,
  InputAdornment,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Tooltip,
  Snackbar,
  Alert
} from '@mui/material';
import {
  Search,
  FilterList,
  Image,
  Info,
  Close,
  PhotoLibrary,
  Category,
  RestaurantMenu,
  Person,
  Business,
  Link as LinkIcon,
  CloudDownload,
  Share,
  Delete
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { format } from 'date-fns';
import galleryService from '../../../../services/galleryService';

const MotionDiv = motion.div;

const Gallery = () => {
  const { t } = useTranslation();
  const [gallery, setGallery] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [viewMode, setViewMode] = useState('grid');
  const [selectedImage, setSelectedImage] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [imageToDelete, setImageToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  
  // Create fetchGallery function outside of useEffect so it can be reused
  const fetchGallery = async () => {
    try {
      setLoading(true);
      console.log('🖼️ Gallery component: Fetching gallery data...');
      console.log('🔑 Authentication token present:', !!localStorage.getItem('token'));
      
      const data = await galleryService.getGallery();
      console.log('🖼️ Gallery component: Data received:', data);
      
      if (!data || data.length === 0) {
        console.log('⚠️ Gallery component: No images found or empty array received');
        setGallery([]);
      } else {
        setGallery(data);
      }
      
      setError(null);
    } catch (err) {
      console.error('🖼️ Gallery component: Error fetching gallery:', err);
      let errorMessage = 'Failed to load gallery';
      
      if (err.response) {
        console.error('🖼️ Response error:', {
          status: err.response.status,
          data: err.response.data,
          headers: err.response.headers
        });
        errorMessage = `Server error: ${err.response.status} - ${err.response.data?.message || 'Unknown error'}`;
      } else if (err.request) {
        console.error('🖼️ Request error (no response):', err.request);
        errorMessage = 'No response from server. Please check your connection.';
      } else {
        console.error('🖼️ Request setup error:', err.message);
        errorMessage = `Error: ${err.message}`;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };
  
  // Fetch gallery data on component mount
  useEffect(() => {
    console.log('🖼️ Gallery component: Mounted, initializing fetch...');
    fetchGallery();
  }, []);
  
  // Filter and search gallery items
  const filteredGallery = gallery.filter(item => {
    const matchesSearch = searchTerm === '' || 
      item.entity_name.toLowerCase().includes(searchTerm.toLowerCase());
      
    const matchesFilter = filterType === 'all' || 
      item.image_type === filterType;
      
    return matchesSearch && matchesFilter;
  });
  
  // Handle image click to show details
  const handleImageClick = (item) => {
    setSelectedImage(item);
    setDetailOpen(true);
  };
  
  // Handle detail dialog close
  const handleDetailClose = () => {
    setDetailOpen(false);
  };
  
  // Handle filter change
  const handleFilterChange = (event) => {
    setFilterType(event.target.value);
  };
  
  // Handle view mode change
  const handleViewModeChange = (event, newValue) => {
    setViewMode(newValue);
  };
  
  // Handle delete image request
  const handleDeleteClick = (item, event) => {
    // Prevent the card click event from firing
    event.stopPropagation();
    setImageToDelete(item);
    setDeleteConfirmOpen(true);
  };
  
  // Handle delete confirmation
  const handleDeleteConfirm = async () => {
    if (!imageToDelete) return;
    
    setDeleting(true);
    try {
      console.log('🗑️ Gallery: Deleting image', imageToDelete);
      
      await galleryService.deleteGalleryImage(
        imageToDelete.image_type,
        imageToDelete.entity_id
      );
      
      // Remove the deleted image from the gallery state
      setGallery(prevGallery => 
        prevGallery.filter(item => 
          !(item.image_type === imageToDelete.image_type && 
            item.entity_id === imageToDelete.entity_id)
        )
      );
      
      // Close dialogs
      setDeleteConfirmOpen(false);
      setImageToDelete(null);
      
      console.log('✅ Gallery: Image deleted successfully');
      
      // Show success message
      const imageName = imageToDelete.entity_name;
      const imageTypeName = galleryService.getImageTypeDisplayName(imageToDelete.image_type);
      setSuccessMessage(t('gallery.deleteSuccess', `${imageTypeName} "${imageName}" has been deleted successfully.`));
      setShowSuccessMessage(true);
      
    } catch (error) {
      console.error('❌ Gallery: Error deleting image:', error);
      setError(error.message || 'Failed to delete image');
    } finally {
      setDeleting(false);
    }
  };
  
  // Handle delete cancellation
  const handleDeleteCancel = () => {
    setDeleteConfirmOpen(false);
    setImageToDelete(null);
  };
  
  // Handle success message close
  const handleSuccessMessageClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setShowSuccessMessage(false);
    setSuccessMessage('');
  };
  
  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return format(new Date(dateString), 'MMM dd, yyyy • HH:mm');
  };
  
  return (
    <Box>
      <MotionDiv
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
          <Typography variant="h4" sx={{ fontWeight: 600 }}>
            {t('gallery.title', 'Image Gallery')}
          </Typography>
          
          <Chip
            icon={<PhotoLibrary />}
            label={t('gallery.count', { count: gallery.length }, `${gallery.length} Images`)}
            color="primary"
          />
        </Box>
      </MotionDiv>
      
      {/* Filters and Search */}
      <MotionDiv
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.1 }}
      >
        <Card sx={{ mb: 3 }}>
          <CardContent sx={{ p: 3 }}>
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder={t('gallery.search', 'Search images...')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search />
                      </InputAdornment>
                    ),
                    endAdornment: searchTerm && (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={() => setSearchTerm('')}>
                          <Close fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />
              </Grid>
              
              <Grid item xs={12} sm={6} md={4}>
                <FormControl fullWidth>
                  <InputLabel>{t('gallery.filterByType', 'Filter by type')}</InputLabel>
                  <Select
                    value={filterType}
                    onChange={handleFilterChange}
                    label={t('gallery.filterByType', 'Filter by type')}
                  >
                    <MenuItem value="all">{t('gallery.allTypes', 'All Types')}</MenuItem>
                    <MenuItem value="vendor_logo">{t('gallery.types.vendorLogo', 'Vendor Logo')}</MenuItem>
                    <MenuItem value="profile_image">{t('gallery.types.profileImage', 'Profile Picture')}</MenuItem>
                    <MenuItem value="menu_item_image">{t('gallery.types.menuItemImage', 'Menu Item')}</MenuItem>
                    <MenuItem value="category_image">{t('gallery.types.categoryImage', 'Category')}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} md={4} sx={{ display: 'flex', justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
                <Tabs 
                  value={viewMode}
                  onChange={handleViewModeChange}
                  aria-label="gallery view mode"
                >
                  <Tab 
                    value="grid" 
                    label={t('gallery.gridView', 'Grid View')} 
                    icon={<PhotoLibrary />} 
                  />
                  <Tab 
                    value="list" 
                    label={t('gallery.listView', 'List View')} 
                    icon={<FilterList />} 
                  />
                </Tabs>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </MotionDiv>
      
      {/* Gallery Content */}
      <MotionDiv
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
      >
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 8 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Box sx={{ textAlign: 'center', my: 8 }}>
            <Typography variant="h5" color="error" gutterBottom>
              {error}
            </Typography>
            <Typography color="text.secondary" variant="body2" sx={{ mb: 2 }}>
              Check the browser console for detailed error information.
            </Typography>
            
            {/* Debug Information Section */}
            <Paper sx={{ p: 2, mt: 2, mb: 3, maxWidth: 800, mx: 'auto', bgcolor: 'grey.100' }}>
              <Typography variant="subtitle2" gutterBottom>
                Debug Information
              </Typography>
              <Box component="pre" sx={{ 
                p: 2, 
                bgcolor: 'background.paper', 
                overflow: 'auto',
                fontSize: '0.8rem',
                textAlign: 'left'
              }}>
                {`Authentication: ${localStorage.getItem('token') ? 'Token Present' : 'No Token!'}
API URL: ${import.meta.env.VITE_API_BASE_URL || 'Using default URL'}`}
              </Box>
            </Paper>
            
            <Button 
              variant="contained" 
              color="primary"
              onClick={() => window.location.reload()}
              sx={{ mt: 2, mr: 2 }}
            >
              {t('gallery.retry', 'Retry Loading')}
            </Button>
            
            <Button 
              variant="outlined"
              onClick={() => {
                console.log('Manual gallery fetch triggered');
                fetchGallery();
              }}
            >
              Try Again with Debug
            </Button>
          </Box>
        ) : filteredGallery.length === 0 ? (
          <Box sx={{ textAlign: 'center', my: 8 }}>
            <Image sx={{ fontSize: 60, color: 'text.secondary', opacity: 0.5 }} />
            <Typography sx={{ mt: 2, color: 'text.secondary' }}>
              {searchTerm || filterType !== 'all' 
                ? t('gallery.noMatchingImages', 'No images match your filters') 
                : t('gallery.noImagesFound', 'No images found in your gallery')}
            </Typography>
            {(searchTerm || filterType !== 'all') && (
              <Button
                variant="text"
                onClick={() => {
                  setSearchTerm('');
                  setFilterType('all');
                }}
                sx={{ mt: 2 }}
              >
                {t('gallery.clearFilters', 'Clear Filters')}
              </Button>
            )}
          </Box>
        ) : viewMode === 'grid' ? (
          // Grid View
          <Grid container spacing={3}>
            {filteredGallery.map((item, index) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={`${item.entity_type}-${item.entity_id}`}>
                <MotionDiv
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.05 }}
                  whileHover={{ y: -5, transition: { duration: 0.2 } }}
                >
                  <Card 
                    sx={{ 
                      height: '100%', 
                      display: 'flex', 
                      flexDirection: 'column',
                      cursor: 'pointer',
                      '&:hover': {
                        boxShadow: 6
                      }
                    }}
                    onClick={() => handleImageClick(item)}
                  >
                    <Box sx={{ position: 'relative' }}>
                      <Box
                        component="img"
                        src={item.image_url}
                        alt={item.entity_name}
                        sx={{
                          width: '100%',
                          height: 200,
                          objectFit: 'cover',
                          objectPosition: 'center'
                        }}
                        onError={(e) => {
                          e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDIwMCAyMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIyMDAiIGhlaWdodD0iMjAwIiBmaWxsPSIjZjVmNWY1Ii8+CjxwYXRoIGQ9Ik0xMCA5MFY2MEgyNVY5MEgxMFoiIGZpbGw9IiNjY2MiLz4KPHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDIwMCAyMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIyMDAiIGhlaWdodD0iMjAwIiBmaWxsPSIjZjVmNWY1Ii8+Cjx0ZXh0IHg9IjEwMCIgeT0iMTA1IiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBmb250LWZhbWlseT0iQXJpYWwsIHNhbnMtc2VyaWYiIGZvbnQtc2l6ZT0iMTQiIGZpbGw9IiM5OTkiPkltYWdlIE5vdCBGb3VuZDwvdGV4dD4KPHN2Zz4K';
                        }}
                      />
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 10,
                          right: 10,
                          display: 'flex',
                          gap: 0.5
                        }}
                      >
                        <Chip
                          size="small"
                          icon={
                            item.image_type === 'vendor_logo' ? <Business fontSize="small" /> :
                            item.image_type === 'profile_image' ? <Person fontSize="small" /> :
                            item.image_type === 'menu_item_image' ? <RestaurantMenu fontSize="small" /> :
                            <Category fontSize="small" />
                          }
                          label={galleryService.getImageTypeDisplayName(item.image_type)}
                          sx={{
                            bgcolor: 'rgba(0, 0, 0, 0.6)',
                            color: 'white',
                            fontWeight: 500,
                            backdropFilter: 'blur(4px)'
                          }}
                        />
                        
                        {item.usage_count > 1 && (
                          <Tooltip title={t('gallery.usedInMultipleItems', 'This image is used in multiple places')}>
                            <Chip
                              size="small"
                              icon={<LinkIcon fontSize="small" />}
                              label={item.usage_count}
                              sx={{
                                bgcolor: 'rgba(25, 118, 210, 0.8)',
                                color: 'white',
                                fontWeight: 500,
                                backdropFilter: 'blur(4px)'
                              }}
                            />
                          </Tooltip>
                        )}
                      </Box>
                      
                      {/* Delete button overlay - appears on hover */}
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 10,
                          left: 10,
                          opacity: 0,
                          transition: 'opacity 0.2s',
                          '.MuiCard-root:hover &': {
                            opacity: 1
                          }
                        }}
                      >
                        <Tooltip title={t('gallery.deleteImage', 'Delete Image')}>
                          <IconButton
                            size="small"
                            onClick={(e) => handleDeleteClick(item, e)}
                            sx={{
                              bgcolor: 'rgba(244, 67, 54, 0.8)',
                              color: 'white',
                              backdropFilter: 'blur(4px)',
                              '&:hover': {
                                bgcolor: 'rgba(244, 67, 54, 0.9)',
                              }
                            }}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                    <CardContent sx={{ flexGrow: 1, pt: 2 }}>
                      <Typography variant="subtitle1" noWrap gutterBottom>
                        {item.entity_name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {formatDate(item.modified_at)}
                      </Typography>
                    </CardContent>
                  </Card>
                </MotionDiv>
              </Grid>
            ))}
          </Grid>
        ) : (
          // List View
          <Paper variant="outlined">
            <List disablePadding>
              {filteredGallery.map((item, index) => (
                <React.Fragment key={`${item.entity_type}-${item.entity_id}`}>
                  {index > 0 && <Divider />}
                  <ListItem 
                    button 
                    onClick={() => handleImageClick(item)}
                    sx={{
                      py: 2,
                      '&:hover': { bgcolor: 'action.hover' }
                    }}
                  >
                    <ListItemAvatar>
                      <Avatar
                        src={item.image_url}
                        variant="rounded"
                        sx={{ width: 56, height: 56 }}
                        onError={(e) => {
                          e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNTYiIGhlaWdodD0iNTYiIHZpZXdCb3g9IjAgMCA1NiA1NiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjU2IiBoZWlnaHQ9IjU2IiBmaWxsPSIjZjVmNWY1Ii8+Cjx0ZXh0IHg9IjI4IiB5PSIzMCIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZm9udC1mYW1pbHk9IkFyaWFsLCBzYW5zLXNlcmlmIiBmb250LXNpemU9IjEwIiBmaWxsPSIjOTk5Ij5FcnJvcjwvdGV4dD4KPHN2Zz4K';
                        }}
                      />
                    </ListItemAvatar>
                    <ListItemText
                      primary={item.entity_name}
                      secondary={
                        <>
                          <Chip
                            size="small"
                            icon={
                              item.image_type === 'vendor_logo' ? <Business fontSize="small" /> :
                              item.image_type === 'profile_image' ? <Person fontSize="small" /> :
                              item.image_type === 'menu_item_image' ? <RestaurantMenu fontSize="small" /> :
                              <Category fontSize="small" />
                            }
                            label={galleryService.getImageTypeDisplayName(item.image_type)}
                            sx={{ mr: 1, mb: 1 }}
                          />
                          {item.usage_count > 1 && (
                            <Chip
                              size="small"
                              icon={<LinkIcon fontSize="small" />}
                              label={`${t('gallery.used', 'Used')} ${item.usage_count} ${t('gallery.times', 'times')}`}
                              color="primary"
                              variant="outlined"
                              sx={{ mr: 1, mb: 1 }}
                            />
                          )}
                          <Typography variant="body2" component="span" color="text.secondary" display="block">
                            {formatDate(item.modified_at)}
                          </Typography>
                        </>
                      }
                      sx={{ ml: 1 }}
                    />
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Tooltip title={t('gallery.deleteImage', 'Delete Image')}>
                        <IconButton
                          color="error"
                          onClick={(e) => handleDeleteClick(item, e)}
                        >
                          <Delete />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('gallery.viewDetails', 'View Details')}>
                        <IconButton>
                          <Info />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </ListItem>
                </React.Fragment>
              ))}
            </List>
          </Paper>
        )}
      </MotionDiv>
      
      {/* Image Detail Dialog */}
      {selectedImage && (
        <Dialog
          open={detailOpen}
          onClose={handleDetailClose}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6">
                {selectedImage.entity_name}
              </Typography>
              <IconButton onClick={handleDetailClose} size="small">
                <Close />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent dividers>
            <Grid container spacing={3}>
              <Grid item xs={12} md={8}>
                <Box 
                  sx={{
                    width: '100%',
                    height: { xs: 300, md: 400 },
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    bgcolor: 'black',
                    borderRadius: 1,
                    overflow: 'hidden'
                  }}
                >
                  <Box
                    component="img"
                    src={selectedImage.image_url}
                    alt={selectedImage.entity_name}
                    sx={{
                      maxWidth: '100%',
                      maxHeight: '100%',
                      objectFit: 'contain'
                    }}
                    onError={(e) => {
                      e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjQwMCIgdmlld0JveD0iMCAwIDQwMCA0MDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSI0MDAiIGhlaWdodD0iNDAwIiBmaWxsPSIjZjVmNWY1Ii8+Cjx0ZXh0IHg9IjIwMCIgeT0iMjEwIiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBmb250LWZhbWlseT0iQXJpYWwsIHNhbnMtc2VyaWYiIGZvbnQtc2l6ZT0iMTgiIGZpbGw9IiM5OTkiPkltYWdlIE5vdCBGb3VuZDwvdGV4dD4KPHN2Zz4K';
                    }}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="h6" gutterBottom>
                  {t('gallery.imageDetails', 'Image Details')}
                </Typography>
                <List>
                  <ListItem divider>
                    <ListItemText 
                      primary={t('gallery.imageType', 'Type')} 
                      secondary={galleryService.getImageTypeDisplayName(selectedImage.image_type)} 
                    />
                  </ListItem>
                  <ListItem divider>
                    <ListItemText 
                      primary={t('gallery.usedIn', 'Used In')} 
                      secondary={`${selectedImage.entity_type}: ${selectedImage.entity_name}`} 
                    />
                  </ListItem>
                  <ListItem divider>
                    <ListItemText 
                      primary={t('gallery.usageCount', 'Usage Count')} 
                      secondary={selectedImage.usage_count} 
                    />
                  </ListItem>
                  <ListItem divider>
                    <ListItemText 
                      primary={t('gallery.lastModified', 'Last Modified')} 
                      secondary={formatDate(selectedImage.modified_at)} 
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText 
                      primary={t('gallery.imageUrl', 'Image URL')} 
                      secondary={
                        <Box 
                          component="div" 
                          sx={{ 
                            wordBreak: 'break-all',
                            maxHeight: 60,
                            overflow: 'auto',
                            fontSize: '0.875rem'
                          }}
                        >
                          {selectedImage.image_url}
                        </Box>
                      } 
                    />
                  </ListItem>
                </List>
                
                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    {t('gallery.actions', 'Actions')}
                  </Typography>
                  <Grid container spacing={1}>
                    <Grid item xs={6}>
                      <Button 
                        variant="outlined" 
                        fullWidth
                        startIcon={<CloudDownload />}
                        onClick={() => window.open(selectedImage.image_url, '_blank')}
                      >
                        {t('gallery.download', 'Download')}
                      </Button>
                    </Grid>
                    <Grid item xs={6}>
                      <Button 
                        variant="outlined" 
                        fullWidth
                        color="primary"
                        startIcon={<Share />}
                        onClick={() => navigator.clipboard.writeText(selectedImage.image_url)}
                      >
                        {t('gallery.copyUrl', 'Copy URL')}
                      </Button>
                    </Grid>
                  </Grid>
                </Box>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button 
              onClick={handleDetailClose}
            >
              {t('common.close', 'Close')}
            </Button>
          </DialogActions>
        </Dialog>
      )}
      
      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={handleDeleteCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {t('gallery.confirmDelete', 'Confirm Delete')}
        </DialogTitle>
        <DialogContent>
          {imageToDelete && (
            <Box>
              <Typography sx={{ mb: 2 }}>
                {t('gallery.deleteConfirmMessage', 'Are you sure you want to delete this image? This action cannot be undone.')}
              </Typography>
              
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Box
                  component="img"
                  src={imageToDelete.image_url}
                  alt={imageToDelete.entity_name}
                  sx={{
                    width: 80,
                    height: 80,
                    objectFit: 'cover',
                    borderRadius: 1
                  }}
                  onError={(e) => {
                    e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iODAiIGhlaWdodD0iODAiIHZpZXdCb3g9IjAgMCA4MCA4MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjgwIiBoZWlnaHQ9IjgwIiBmaWxsPSIjZjVmNWY1Ii8+Cjx0ZXh0IHg9IjQwIiB5PSI0NSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZm9udC1mYW1pbHk9IkFyaWFsLCBzYW5zLXNlcmlmIiBmb250LXNpemU9IjEyIiBmaWxsPSIjOTk5Ij5FcnJvcjwvdGV4dD4KPHN2Zz4K';
                  }}
                />
                <Box>
                  <Typography variant="subtitle1" fontWeight="medium">
                    {imageToDelete.entity_name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {galleryService.getImageTypeDisplayName(imageToDelete.image_type)}
                  </Typography>
                  {imageToDelete.usage_count > 1 && (
                    <Typography variant="body2" color="warning.main" sx={{ mt: 1 }}>
                      ⚠️ {t('gallery.deleteWarning', 'This image is used in {{count}} places and will be removed from all of them.', { count: imageToDelete.usage_count })}
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handleDeleteCancel}
            disabled={deleting}
          >
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button 
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} /> : <Delete />}
          >
            {deleting ? t('gallery.deleting', 'Deleting...') : t('common.delete', 'Delete')}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Success Message Snackbar */}
      <Snackbar
        open={showSuccessMessage}
        autoHideDuration={4000}
        onClose={handleSuccessMessageClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleSuccessMessageClose} 
          severity="success" 
          variant="filled"
          sx={{ width: '100%' }}
        >
          {successMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Gallery;
